import { createAsyncThunk, createEntityAdapter, createSlice, Dictionary } from '@reduxjs/toolkit';
import { ReduxEntity, UserContact } from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { RootState } from 'Douleutaras/redux/store';

const CONTACTS_ENDPOINT = 'api/public/contact';

const contactsAdapter = createEntityAdapter<UserContact>({
  selectId: contact => contact.id
});

export const getUserContacts = createAsyncThunk(
  'user/contacts/get',
  async (arg = undefined, api) => {
    const response = await makeRequest(
      `${CONTACTS_ENDPOINT}/`,
      { method: 'GET' }
    )(api);

    return response.data;
  }
);

export const updateUserContact = createAsyncThunk(
  'user/contacts/update',
  async ({ contactId, data }: { contactId: string; data: Dictionary<boolean | string | number> }, api) => {
    const { updatePublicStatus } = contactsSlice.actions;
    const response = await makeRequest(
      `${CONTACTS_ENDPOINT}/${contactId}/`,
      {
        method: 'PATCH',
        body: {
          ...data
        }
      }
    )(api);
    api.dispatch(updatePublicStatus(response.data));
  }
);

export const deleteUserContact = createAsyncThunk(
  'user/contacts/delete',
  async ({ contactId }: { contactId: number }, api) => {
    const { deleteContact } = contactsSlice.actions;
    await makeRequest(
      `${CONTACTS_ENDPOINT}/${contactId}/`,
      {
        method: 'DELETE'
      }
    )(api);
    api.dispatch(deleteContact(contactId));
  }
);

export const createUserContact = createAsyncThunk(
  'user/contacts/create',
  async ({ data }: { data: Dictionary<string> }, api) => {
    const { addContact } = contactsSlice.actions;
    const response = await makeRequest(
      `${CONTACTS_ENDPOINT}/`,
      {
        method: 'POST',
        body: {
          ...data
        }
      }
    )(api);
    api.dispatch(addContact(response.data));
  }
);

// @ts-ignore
export const contactsSlice = createSlice({
  name: 'contacts',
  initialState: contactsAdapter.getInitialState({
    loading: false,
    error: false,
    entities: {}
  } as ReduxEntity<UserContact>),
  reducers: {
    updatePublicStatus: (state, action) => {
      state.entities[action.payload.id] = action.payload;
    },
    deleteContact: (state, action) => {
      contactsAdapter.removeOne(state, action.payload);
    },
    addContact: (state, action) => {
      contactsAdapter.addOne(state, action.payload);
    }
  },
  extraReducers: builder => {
    builder.addCase(getUserContacts.fulfilled, (state, action) => {
      contactsAdapter.upsertMany(state, action.payload || []);
    });
  }
});

export const contactsSelectors = contactsAdapter.getSelectors((state: RootState) => state.contacts);
